import {
  BiDashboardGhost,
  BiDashboardHost,
} from '@euler/app/components/message/widgets/bi-dashboard-changes/BiDashboardHostContext';
import { useCreation, useLocalObservable } from '@euler/utils/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { BehaviorSubject, filter, map } from 'rxjs';

/**
 * What the chat's change cards see of this dashboard, and the ghost they
 * ask it to draw: the newest pending proposal still shown.
 */
export function useBiGhostHost(
  dashboardId: string,
  /** The revision on screen; null while loading. */
  revision: number | null,
  /** Closes a full-screen chat; null when the chat sits beside. */
  reveal: (() => void) | null,
) {
  const ghosts$ = useCreation(
    () => new BehaviorSubject<BiDashboardGhost[]>([]),
    [],
  );
  const revisionOnScreen$ = useCreation(
    () => new BehaviorSubject<number | null>(null),
    [],
  );
  useEffect(
    () => revisionOnScreen$.next(revision),
    [revision, revisionOnScreen$],
  );
  // Cards register in the order their reads land, so the newest proposal
  // is picked by when it was made.
  const ghost = useLocalObservable(
    () =>
      ghosts$.pipe(
        map(list =>
          list.reduce<BiDashboardGhost | null>(
            (newest, item) =>
              !newest ||
              Date.parse(item.createdAt) > Date.parse(newest.createdAt)
                ? item
                : newest,
            null,
          ),
        ),
      ),
    null,
    [ghosts$],
  );
  const showGhost = useCallback(
    (value: BiDashboardGhost) => {
      ghosts$.next([...ghosts$.getValue(), value]);
      return () =>
        ghosts$.next(ghosts$.getValue().filter(item => item !== value));
    },
    [ghosts$],
  );
  const host = useMemo<BiDashboardHost>(
    () => ({
      dashboardId,
      revision$: revisionOnScreen$.pipe(
        filter((value): value is number => value != null),
      ),
      showGhost,
      reveal,
    }),
    [dashboardId, revisionOnScreen$, showGhost, reveal],
  );
  return { host, ghost };
}
